import {
  Badge,
  Button,
  cn,
  ConfirmDialog,
  EmptyState,
  Input,
  PageHeader,
  Skeleton,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@repo/ui'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  BookOpen,
  Download,
  FileText,
  Paperclip,
  Pencil,
  Plus,
  Search,
  Trash2,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import { useAuth } from '@/hooks/use-auth'
import {
  useDeleteKnowledgeBase,
  useKnowledgeBases,
} from '@/hooks/use-knowledge'
import type { KnowledgeArticleType, KnowledgeBase } from '@/types/knowledge'

const TAB_LIST_CLASS = 'h-auto gap-1 rounded-none border-0 bg-transparent p-0'
const TAB_TRIGGER_CLASS =
  'h-auto flex-none -mb-px rounded-none border-x-0 border-t-0 border-b-2 border-transparent bg-transparent px-4 py-2.5 text-[15px] font-medium shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none'

export const Route = createFileRoute('/_app/conhecimento/')({
  component: KnowledgePage,
})

type TabKey = 'all' | 'TEXT' | 'DOCUMENT'

function KnowledgePage() {
  const navigate = useNavigate()
  const { currentOrgId } = useAuth()
  const [tab, setTab] = useState<TabKey>('all')
  const [search, setSearch] = useState('')
  const [deleting, setDeleting] = useState<KnowledgeBase | null>(null)

  const { data: response, isLoading } = useKnowledgeBases(currentOrgId)
  const deleteKb = useDeleteKnowledgeBase(currentOrgId)

  const bases = useMemo(() => response?.data ?? [], [response])
  const totals = useMemo(() => {
    let text = 0
    let document = 0
    for (const base of bases) {
      for (const article of base.articles ?? []) {
        if (article.type === 'DOCUMENT') document += 1
        else text += 1
      }
    }
    return { all: bases.length, text, document }
  }, [bases])

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    return bases.filter((base) => {
      const articles = base.articles ?? []
      if (tab !== 'all' && !articles.some((article) => article.type === tab)) {
        return false
      }
      if (!query) return true
      return (
        base.name.toLowerCase().includes(query) ||
        (base.description ?? '').toLowerCase().includes(query) ||
        articles.some(
          (article) =>
            article.title.toLowerCase().includes(query) ||
            article.content.toLowerCase().includes(query)
        )
      )
    })
  }, [bases, search, tab])

  function openNew(defaultType: KnowledgeArticleType = 'TEXT') {
    void navigate({
      to: '/conhecimento/novo',
      search: defaultType === 'DOCUMENT' ? { tipo: 'DOCUMENT' } : {},
    })
  }

  function openEdit(base: KnowledgeBase) {
    void navigate({
      to: '/conhecimento/$knowledgeBaseId',
      params: { knowledgeBaseId: base.id },
    })
  }

  async function removeBase(base: KnowledgeBase) {
    await deleteKb.mutateAsync(base.id)
    toast.success('Conhecimento removido')
    setDeleting(null)
  }

  if (!currentOrgId) {
    return (
      <div className="text-muted-foreground flex flex-1 items-center justify-center">
        Selecione uma organização
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageHeader
        icon={BookOpen}
        title="Conhecimento"
        description="Base de textos, FAQs e documentos usada pelos agentes IA"
        actions={
          <>
            <Button type="button" onClick={() => openNew()}>
              <Plus className="h-4 w-4" />
              Adicionar conhecimento
            </Button>
          </>
        }
      />

      <div className="min-h-0 flex-1 overflow-auto">
        <div className="mx-auto max-w-6xl px-6 py-8">
          <div className="mb-6 flex flex-wrap items-end justify-between gap-3 border-b">
            <Tabs
              value={tab}
              onValueChange={(value) => setTab(value as TabKey)}
              className="w-auto"
            >
              <TabsList className={TAB_LIST_CLASS}>
                <TabsTrigger
                  value="all"
                  className={cn(TAB_TRIGGER_CLASS, 'gap-1.5')}
                >
                  <BookOpen className="h-4 w-4" />
                  Todos
                  <Badge variant="secondary" className="ml-1 text-xs">
                    {totals.all}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger
                  value="TEXT"
                  className={cn(TAB_TRIGGER_CLASS, 'gap-1.5')}
                >
                  <FileText className="h-4 w-4" />
                  Textos
                  <Badge variant="secondary" className="ml-1 text-xs">
                    {totals.text}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger
                  value="DOCUMENT"
                  className={cn(TAB_TRIGGER_CLASS, 'gap-1.5')}
                >
                  <Paperclip className="h-4 w-4" />
                  Arquivos
                  <Badge variant="secondary" className="ml-1 text-xs">
                    {totals.document}
                  </Badge>
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative mb-2 min-w-64 md:max-w-xs">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar..."
                className="pl-9"
              />
            </div>
          </div>

          {isLoading ? (
            <KnowledgeSkeleton />
          ) : filtered.length === 0 ? (
            <EmptyKnowledge
              tab={tab}
              onCreate={() => openNew(tab === 'DOCUMENT' ? 'DOCUMENT' : 'TEXT')}
            />
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((base) => (
                <KnowledgeCard
                  key={base.id}
                  base={base}
                  onEdit={() => openEdit(base)}
                  onDelete={() => setDeleting(base)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="Remover este conhecimento?"
        description={
          deleting
            ? `"${deleting.name}" será removido. Agentes vinculados deixarão de usá-lo.`
            : ''
        }
        confirmLabel="Remover"
        destructive
        confirmDisabled={deleteKb.isPending}
        onConfirm={() => deleting && void removeBase(deleting)}
      />
    </div>
  )
}

function KnowledgeCard({
  base,
  onEdit,
  onDelete,
}: {
  base: KnowledgeBase
  onEdit: () => void
  onDelete: () => void
}) {
  const articles = base.articles ?? []
  const primary = articles[0]
  const hasDocument = articles.some((article) => article.type === 'DOCUMENT')

  return (
    <article className="bg-card rounded-md border p-5">
      <div className="flex items-start justify-between gap-2">
        <div className="bg-primary/10 flex h-8 w-8 items-center justify-center rounded-md">
          {hasDocument ? (
            <Paperclip className="text-primary h-4 w-4" />
          ) : (
            <FileText className="text-primary h-4 w-4" />
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button type="button" size="icon" variant="ghost" onClick={onEdit}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button type="button" size="icon" variant="ghost" onClick={onDelete}>
            <Trash2 className="text-destructive h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      <h2 className="mt-3 line-clamp-2 text-base font-semibold tracking-tight">
        {base.name}
      </h2>
      {base.description ? (
        <p className="text-muted-foreground mt-1 line-clamp-2 text-sm">
          {base.description}
        </p>
      ) : null}
      {primary?.type === 'TEXT' ? (
        <p className="text-muted-foreground mt-3 line-clamp-3 text-sm whitespace-pre-wrap">
          {primary.content}
        </p>
      ) : null}
      {primary?.type === 'DOCUMENT' && primary.fileUrl ? (
        <a
          href={primary.fileUrl}
          target="_blank"
          rel="noreferrer"
          className="text-primary mt-3 inline-flex items-center gap-1.5 text-xs font-medium hover:underline"
        >
          <Download className="h-3.5 w-3.5" />
          Abrir arquivo
          {primary.fileSize ? (
            <span className="text-muted-foreground">
              · {(primary.fileSize / 1024).toFixed(0)} KB
            </span>
          ) : null}
        </a>
      ) : null}
      <div className="mt-4 flex items-center justify-between">
        <Badge variant="secondary" className="text-xs uppercase">
          {articles.length} item(ns)
        </Badge>
        <span className="text-muted-foreground text-xs">
          {new Date(base.updatedAt).toLocaleDateString('pt-BR')}
        </span>
      </div>
    </article>
  )
}

function EmptyKnowledge({
  tab,
  onCreate,
}: {
  tab: TabKey
  onCreate: () => void
}) {
  return (
    <EmptyState
      icon={tab === 'DOCUMENT' ? Paperclip : FileText}
      title="Nada por aqui ainda"
      description={
        tab === 'DOCUMENT'
          ? 'Faça upload de PDFs, planilhas e outros documentos para os agentes consultarem.'
          : 'Adicione textos com informações que seus agentes IA podem usar para responder.'
      }
      action={
        <Button type="button" variant="outline" size="sm" onClick={onCreate}>
          <Plus className="h-3.5 w-3.5" />
          Adicionar
        </Button>
      }
    />
  )
}

function KnowledgeSkeleton() {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 6 }).map((_, index) => (
        <Skeleton key={index} className="h-52 w-full rounded-md" />
      ))}
    </div>
  )
}
